import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { Column, TableRow, TableRowWithId } from "./table-interfaces";
import { FilterCriterion, SortData, SortDirection } from "./table-types";
import applyElementPolyfills from "../../polyfills/element";

applyElementPolyfills();

@Component({
    tag: "uic-table",
    styleUrl: "table.scss",
    shadow: false
})
export class Table {
    private baseClass = "uic-table";
    private thead: HTMLElement = null;
    private tbody: HTMLElement = null;
    private renderCoeff = 2;
    private scroller: HTMLDivElement = null;
    private scrollEventTargetElement: Window | HTMLElement;
    private readonly debouncedUpdateTableColumns = debounce(() => this.updateTableColumns(), 100);
    /** content observer to watch for attribute changes in table column components  */
    private readonly contentObserver: MutationObserver = new MutationObserver(this.debouncedUpdateTableColumns);

    /** DOM element */
    @Element() element: HTMLUicTableElement;

    /** the table should always be at least 100% wide */
    @Prop() readonly fullWidth: boolean = false;
    /** the object containing the table data coming from the prop */
    @Prop() readonly data?: TableRow[];
    /** this enables the self-sorting feature for the table.
     *  If you display incomplete data (i.e. with pagination) you should do the sorting
     *  by yourself using the uicTableSort events */
    @Prop() readonly selfSorting: boolean = false;
    /** defines the initial sorting criteria which should be applied once the data is loaded */
    @Prop() readonly initialSortingCriterion?: SortData;
    /** if set, the table will lazy render the rows */
    @Prop() readonly lazyRender: boolean = false;
    /** (for lazy-render) target element to wich the scroll listener will be attached */
    @Prop() readonly scrollEventTarget: string = "";
    /** will be taken as row height for lazy loading calculations */
    @Prop() readonly lazyRowHeight: number = 67;
    /** if set, the thead of the table will be set to a sticky position */
    @Prop() readonly stickyThead: boolean = false;

    /** contains the data of the columns to be rendered */
    @State() tableColumns: Column[] = [];
    /** contains the data of the columns to be rendered */
    // eslint-disable-next-line
    @State() tableColumnsMethods: any[] = [];
    /** contains the ids of the selected rows */
    @State() selectedRows: string[] = [];
    /** data in the final sorted order */
    @State() displayData: TableRowWithId[] = [];

    /** data in the final sorted order */
    @State() originalData: TableRowWithId[] = [];

    /** used for lazy rendering */
    @State() firstRowHeight = 0;
    /** used for lazy rendering */
    @State() lastRowHeight = 0;
    /** used for lazy rendering */
    @State() step = 0;
    /** used for lazy rendering */
    @State() startIndex = 0;
    /** used for lazy rendering */
    @State() sortCriterion: SortData;
    /** used for lazy rendering */
    @State() filterCriterionMap: Map<string, FilterCriterion> = new Map();

    /** triggered when a row was selected */
    @Event({
        eventName: "uicRowsSelected",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    rowsSelected: EventEmitter;

    /** triggered when a row was clicked */
    @Event({
        eventName: "uicRowClick",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    rowClick: EventEmitter;

    @Watch("data")
    onDataChanged() {
        const rows = this.getDataWithInternalIds();
        if (rows === undefined) {
            return;
        }
        this.originalData = rows;
        this.displayData = this.sortRowsByInitialCriterion(rows);
        this.setSelectedRows(this.displayData);
        if (this.lazyRender) {
            this.initTable();
        }
    }

    @Watch("scrollEventTarget")
    onScrollEventTargetChanged() {
        this.removeScrollListener();
        this.addScrollListener();
    }

    @Watch("selectedRows")
    emitSelectedRows() {
        this.rowsSelected.emit({
            selectedRows: this.displayData.filter((row: TableRowWithId) => this.selectedRows.includes(row._uicTableInternalId))
        });
    }

    @Watch("startIndex")
    updateHeights() {
        this.firstRowHeight = this.startIndex * this.lazyRowHeight;
        this.lastRowHeight = Math.floor(
            this.data.length * this.lazyRowHeight - this.step * this.lazyRowHeight - this.firstRowHeight
        );
    }

    /** Listen to table sorting events */
    @Listen("uicTableSort")
    onSortRow(e: CustomEvent) {
        if (!this.selfSorting) {
            return;
        }
        const sorter = e.target as HTMLUicTableHeadSortElement;
        const { sortedDirection } = sorter;
        this.sortCriterion = { sortProperty: e.detail.name, direction: sortedDirection };
        this.updateDisplayData();
    }

    /** */
    @Listen("uicTableFilter")
    onFilterRow(e: CustomEvent) {
        const field = e.detail.field;
        const items: string[] = e.detail.items;
        if (items.length < 1) {
            this.filterCriterionMap.delete(field);
        } else {
            this.filterCriterionMap.set(field, { field: field, values: items });
        }
        this.updateDisplayData();
    }

    private filter(rowsToSort: TableRowWithId[], filterCriterion: FilterCriterion): TableRowWithId[] {
        return rowsToSort.filter((row) => filterCriterion.values.includes(row[filterCriterion.field]));
    }
    private updateDisplayData() {
        let filteredData = Array.from(this.originalData);
        this.filterCriterionMap.forEach((criterion) => {
            filteredData = this.filter(filteredData, criterion);
        });
        if (this.sortCriterion) {
            this.displayData = this.goodSort(filteredData);
        } else {
            this.displayData = filteredData;
        }
    }
    /** Scroll to an entry if `lazy-render` is enabled */
    @Method()
    async scrollToVirtualElement(name: string | number, value: string | number, behavior: "auto" | "smooth" = "smooth") {
        if (!this.lazyRender) {
            return;
        }
        let elementIndex = -1;
        this.displayData.find((element, index) => {
            if (element[name] === value) {
                elementIndex = index;
            }
            return element[name] === value;
        });

        if (elementIndex === -1) {
            return;
        }
        const scrollerY =
            this.thead.clientHeight + elementIndex * this.lazyRowHeight - (this.stickyThead ? this.thead?.clientHeight || 0 : 0);

        this.scroller.style.top = `${scrollerY}px`;
        this.scroller.scrollIntoView({
            behavior: behavior
        });
    }

    componentWillLoad() {
        this.updateTableColumns().then(() => this.onDataChanged());
    }

    componentDidLoad() {
        if (this.lazyRender) {
            window.setTimeout(this.initTable, 0);
        }
    }

    connectedCallback() {
        this.contentObserver.observe(this.element, {
            childList: true,
            subtree: true,
            attributes: true
        });
        this.addScrollListener();
    }

    disconnectedCallback() {
        this.contentObserver.disconnect();
        this.removeScrollListener();
    }

    private initTable = () => {
        if (this.tbody === null) {
            return;
        }
        const tableTop = this.tbody.getBoundingClientRect().top;
        const viewPortY = document.documentElement.clientHeight;
        const numRows = this.data?.length || 0;
        if (tableTop > 0) {
            this.step = Math.floor((viewPortY * this.renderCoeff - tableTop) / this.lazyRowHeight);
            this.startIndex = 0;
        } else {
            this.step = Math.floor((viewPortY * this.renderCoeff) / this.lazyRowHeight);
            this.startIndex = Math.floor(-tableTop / this.lazyRowHeight);
        }
        this.firstRowHeight = this.startIndex * this.lazyRowHeight;
        this.lastRowHeight = Math.max(this.firstRowHeight, numRows * this.lazyRowHeight - this.step * this.lazyRowHeight);
    };

    private onScroll = () => {
        if (!Array.isArray(this.data)) {
            return;
        }
        const top = getTopDistance(this.tbody, this.scrollEventTargetElement);
        const viewportY = document.documentElement.clientHeight;
        if (top < 0) {
            this.step = Math.floor((viewportY * this.renderCoeff) / this.lazyRowHeight);
            this.startIndex = Math.min(
                this.data.length - this.step > 1 ? this.data.length - this.step : 1,
                Math.floor(-top / this.lazyRowHeight)
            );
        } else {
            this.startIndex = 0;
            this.step = Math.floor((viewportY * this.renderCoeff - top) / this.lazyRowHeight);
        }
    };

    private addScrollListener = () => {
        this.removeScrollListener();
        this.scrollEventTargetElement = this.scrollEventTarget
            ? this.element.closest<HTMLElement>(this.scrollEventTarget) || window
            : window;
        if (this.lazyRender && this.scrollEventTargetElement) {
            this.scrollEventTargetElement.addEventListener("scroll", this.onScroll, {
                passive: true
            });
        }
    };

    private removeScrollListener = () => {
        if (this.scrollEventTargetElement) {
            this.scrollEventTargetElement.removeEventListener("scroll", this.onScroll);
        }
    };

    /** updates the tableColumn state by getting the props of the uic-table-columns */
    private async updateTableColumns() {
        const children: HTMLUicTableColumnElement[] = [...this.element.children] as HTMLUicTableColumnElement[];
        const columns: Column[] = children.filter((child) => typeof child.getColumnMethods === "function") as Column[];
        const tableColumnsMethods = await Promise.all(columns.map((col) => col.getColumnMethods()));
        this.tableColumns = columns;
        this.tableColumnsMethods = tableColumnsMethods;
    }

    private getDataWithInternalIds(): TableRowWithId[] | undefined {
        return this.data?.map((row, i) => ({
            ...row,
            _uicTableInternalId: `${i}`
        }));
    }

    private setSelectedRows(rows: TableRowWithId[]) {
        this.selectedRows = rows
            .filter((tableRow) => {
                return tableRow.selected;
            })
            .map((tableRow) => tableRow._uicTableInternalId);
    }

    /** Triggers sorting of defined columns if `self-sorting` is enabled */
    private sortRowsByInitialCriterion(rows: TableRowWithId[]) {
        if (!this.selfSorting || this.initialSortingCriterion === undefined) {
            return rows;
        }

        const index = this.tableColumns.findIndex(
            (column) =>
                this.initialSortingCriterion.sortProperty === column.sortField ||
                this.initialSortingCriterion.sortProperty === column.field
        );
        if (index !== -1) {
            return this.sort(index, this.initialSortingCriterion.direction, rows);
        } else {
            return rows;
        }
    }

    private goodSort(rows: TableRowWithId[]) {
        if (this.sortCriterion?.direction === "") {
            return this.getDataWithInternalIds();
        }
        const comparer = (a: TableRow, b: TableRow) => {
            const valA = a[this.sortCriterion.sortProperty].toLocaleString();
            const valB = b[this.sortCriterion.sortProperty].toLocaleString();
            const isNum = !(isNaN(valA) && isNaN(valB));
            return this.sortCriterion.direction === "asc"
                ? valA.localeCompare(valB, undefined, { numeric: isNum })
                : valB.localeCompare(valA, undefined, { numeric: isNum });
        };
        return rows.sort(comparer);
    }

    private sort(columnIndex: number, direction: SortDirection, rows: TableRowWithId[]) {
        const getSortString = this.tableColumnsMethods[columnIndex].getSortString || (() => "");
        if (direction === "") {
            return this.getDataWithInternalIds();
        }
        const comparer = (a: TableRow, b: TableRow) => {
            const strA = getSortString(a);
            const strB = getSortString(b);
            return direction === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
        };
        return rows.sort(comparer);
    }

    private renderRow = (tableRow: TableRowWithId) => {
        return (
            <tr
                class={{
                    ["uic-table__tr"]: true,
                    ["uic-table__tr--selected"]: this.selectedRows.includes(tableRow._uicTableInternalId)
                }}
                key={tableRow._uicTableInternalId}
                onClick={(event) => {
                    this.rowClick.emit({
                        row: tableRow,
                        clickEvent: event
                    });
                }}
            >
                {this.tableColumnsMethods?.map((renderer) => {
                    return renderer.renderCell(tableRow, this);
                })}
            </tr>
        );
    };

    render() {
        const { fullWidth, baseClass, displayData: sortedData, stickyThead } = this;
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--full-width`]: fullWidth,
                    [`${baseClass}--sticky-thead`]: stickyThead
                }}
            >
                <slot />
                {this.lazyRender && <div class={`${baseClass}__scroller`} ref={(el) => (this.scroller = el)} />}
                <div class={`${baseClass}__inner`}>
                    <table class={`${baseClass}__table`}>
                        <thead class={`${baseClass}__thead`} ref={(el) => (this.thead = el)}>
                            <tr class={`${baseClass}__tr-head`}>
                                {this.tableColumnsMethods?.map((renderer) => renderer.renderHeader(this))}
                            </tr>
                        </thead>
                        <tbody ref={(el) => (this.tbody = el)} class={`${baseClass}__tbody`}>
                            {this.lazyRender && (
                                <tr
                                    style={{
                                        height: `${this.firstRowHeight}px`,
                                        maxHeight: `${this.firstRowHeight}px`
                                    }}
                                />
                            )}
                            {this.lazyRender && sortedData.slice(this.startIndex, this.startIndex + this.step).map(this.renderRow)}
                            {this.lazyRender && (
                                <tr
                                    style={{
                                        height: `${this.lastRowHeight}px`,
                                        maxHeight: `${this.lastRowHeight}px`
                                    }}
                                />
                            )}
                            {!this.lazyRender && sortedData.map(this.renderRow)}
                        </tbody>
                    </table>
                </div>
            </Host>
        );
    }
}

const getTopDistance = (el1: HTMLElement, el2: HTMLElement | Window): number => {
    const el1RectTop = el1.getBoundingClientRect().top;
    const el2RectTop: number = el2 === window ? 0 : (el2 as HTMLElement).getBoundingClientRect().top;
    return el1RectTop - el2RectTop;
};
